/**
 * FailingChecksPanel — the rich "why can't I approve this" failure UX.
 *
 * Renders each failing/blocking check with its exact failure detail — down
 * to the failing test/step, `file:line` where available, and the
 * assertion/error message — instead of the raw GitHub merge error Mission
 * Control used to dump verbatim to the operator (e.g. `merging PR #519: PUT
 * https://api.github.com/.../merge: 405 Required status check "validate" is
 * failing. []`). Each check links out to its GitHub details page.
 *
 * Used by ImprovementPRReview both proactively (fetched as soon as the PR's
 * checks aren't all green, to explain the disabled Approve & Deploy button)
 * and reactively (if Approve is attempted anyway and the backend still
 * returns `checks_failing` — the same shape, rendered the same way, instead
 * of a raw error toast).
 *
 * `detailState`/`detailMessage` are OPTIONAL and default to a complete read,
 * because fourteen of this component's fifteen render sites pass the
 * `author_tests_failing` 409 payload, which carries neither. Those sites keep
 * today's output exactly, down to the "empty list renders nothing" rule.
 *
 * Row vocabulary: a `workflow` row is a run that failed with none of its
 * enumerated jobs failing, and is labelled as one rather than presented as a
 * check name; an `unenumerable` row says only that the run's jobs could not be
 * read, and asserts nothing about whether any failed. Nothing here is called
 * *required* — requiredness needs `checks: read`, which this detail route does
 * not hold.
 */
import type { DetailState, FailingCheck } from '../api/client'

export function FailingChecksPanel({
  checks,
  detailState = 'complete',
  detailMessage,
}: {
  checks: FailingCheck[]
  detailState?: DetailState
  detailMessage?: string
}) {
  // An empty list is still nothing to show when the read itself was complete —
  // that is the author-tests path, and the fourteen call sites that pass no
  // state depend on it. An empty list from a REFUSED or CUT-SHORT read is a
  // different thing and has to say so.
  if (checks.length === 0 && detailState === 'complete') return null

  // An `unenumerable` row asserts nothing about whether a job failed, so it is
  // not counted as one. Only the Actions-derived payload carries `kind`; the
  // author-tests payload does not, and its heading has to stay exactly as it
  // is, so the count is only narrowed on rows that came from Actions.
  const actionsShaped = checks.some(c => !!c.kind)
  const failingCount = actionsShaped
    ? checks.filter(c => c.kind !== 'unenumerable').length
    : checks.length

  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-3">
      {failingCount > 0 ? (
        <p className="text-sm font-semibold text-red-800">
          Checks failing — can't approve yet ({failingCount} check{failingCount === 1 ? '' : 's'})
        </p>
      ) : (
        <p className="text-sm font-semibold text-red-800">
          Mission Control could not read which checks are failing
        </p>
      )}
      {detailState !== 'complete' && (
        <p className="text-xs text-red-800 bg-red-100 border border-red-200 rounded px-2 py-1.5">
          {detailState === 'unreadable'
            ? 'This detail could not be read, so nothing below is a complete account of the change.'
            : 'This list is incomplete — part of the detail could not be read, so there may be failures it does not name.'}
          {detailMessage ? ` ${detailMessage}` : ''}
        </p>
      )}
      <div className="space-y-2">
        {checks.map((check, checkIndex) => (
          <div
            key={check.id ?? checkIndex}
            className={
              check.kind === 'unenumerable'
                ? 'rounded border border-slate-200 bg-white p-3'
                : 'rounded border border-red-200 bg-white p-3'
            }
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900">
                {check.kind === 'workflow' || check.kind === 'unenumerable'
                  ? `Workflow: ${check.name}`
                  : check.name}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                {check.kind !== 'unenumerable' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-medium">
                    {check.conclusion || 'failing'}
                  </span>
                )}
                {check.details_url && (
                  <a
                    href={check.details_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    View on GitHub ↗
                  </a>
                )}
              </div>
            </div>

            {check.kind === 'workflow' && (
              <p className="mt-2 text-xs text-gray-600">
                The workflow failed without a failing job — no individual check is named.
              </p>
            )}
            {check.kind === 'unenumerable' && (
              <p className="mt-2 text-xs text-gray-600">
                Mission Control could not read this run's jobs, so nothing is known about
                whether any of them failed.
              </p>
            )}

            {check.kind === 'unenumerable' ? null : check.details.length > 0 ? (
              <ul className="mt-2 space-y-2">
                {check.details.map((d, i) => (
                  <li key={i} className="text-xs border-l-2 border-red-200 pl-2">
                    {(d.path || d.title) && (
                      <div className="font-mono text-gray-500">
                        {d.path}
                        {d.path && d.line ? `:${d.line}` : ''}
                        {d.path && d.title ? ' — ' : ''}
                        {d.title && <span className="text-gray-700">{d.title}</span>}
                      </div>
                    )}
                    {/* A log excerpt runs to twenty lines, and a page can
                        carry several failing checks, so each excerpt scrolls
                        inside its own box rather than pushing the Approve and
                        Deny buttons off the screen. */}
                    <div
                      data-testid="check-detail-message"
                      className="font-mono text-red-700 whitespace-pre-wrap break-words mt-0.5 max-h-64 overflow-y-auto"
                    >
                      {d.message}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-xs text-gray-500">
                No further detail available for this check — see it on GitHub.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
